import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import { useState, useEffect } from 'react'

/**
 * Language Switcher Component
 * Allows users to switch between NL, FR, EN, and DE
 */
function LanguageSwitcher() {
  const { i18n, t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [currentLang, setCurrentLang] = useState(i18n.language)

  const languages = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ]

  // Subscribe to language changes to trigger re-render
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setCurrentLang(lng)
    }

    i18n.on('languageChanged', handleLanguageChanged)
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [i18n])

  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0]

  const changeLanguage = async (langCode) => {
    try {
      // Change language - this will trigger the languageChanged event
      await i18n.changeLanguage(langCode)
      setIsOpen(false)
      
      // Save to localStorage (already handled by i18next-browser-languagedetector)
      // Also save to user preferences via API (if user is logged in)
      try {
        const api = (await import('../services/api')).default
        await api.put('i18n/preferences/language', {
          language_code: langCode
        })
      } catch (error) {
        // Silently fail - language change still works locally
        console.warn('Failed to save language preference to server:', error)
      }
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-current hover:bg-blue-500/10 rounded-lg transition-colors"
        aria-label={t('common.changeLanguage')}
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{currentLanguage.flag}</span>
        <span className="hidden md:inline">{currentLanguage.code.toUpperCase()}</span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown — opent naar BOVEN: de LanguageSwitcher zit in de OnderBalk (onderaan het venster),
              dus naar onder zou off-screen vallen. */}
          <div className="absolute right-0 bottom-full mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
            <div className="py-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    currentLang === lang.code
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                  {currentLang === lang.code && (
                    <span className="ml-auto text-blue-600 dark:text-blue-400">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default LanguageSwitcher
